import { useEffect, useMemo, useState } from "react";
import api from "../../../../../services/api";
import { useWeddingWizard } from "../../../../../context/WeddingWizardContext";
import { SUBCAT_ORDER, SUBCATEGORY_META } from "../utils/constants";

export default function useDessertsData() {
  const { state } = useWeddingWizard();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [activeSubcat, setActiveSubcat] = useState("");

  // NEW: selections now live at state.desserts.selections (array)
  const selections = useMemo(
    () =>
      Array.isArray(state?.desserts?.selections)
        ? state.desserts.selections
        : [],
    [state.desserts]
  );

  const itemById = useMemo(() => {
    const map = new Map();
    for (const it of items) map.set(it._id, it);
    return map;
  }, [items]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/weddings/desserts");
        if (!mounted) return;
        const arr = Array.isArray(data?.items) ? data.items : [];
        setItems(arr);

        const subcats = Array.from(
          new Set(arr.map((i) => i?.subcategory).filter(Boolean))
        );
        const first =
          SUBCAT_ORDER.find((sc) => subcats.includes(sc)) || subcats[0] || "";
        setActiveSubcat(first || "");
      } catch (e) {
        console.error("Load desserts failed", e);
        if (mounted) setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const subtotal = useMemo(() => {
    return selections.reduce((sum, sel) => {
      const it = itemById.get(sel.patisserieId);
      if (!it) return sum;
      const unit = Number(it?.wedding?.price) || 0;
      return sum + unit * (Number(sel.quantity) || 0);
    }, 0);
  }, [selections, itemById]);

  const selectedCountsBySubcat = useMemo(() => {
    const counts = {};
    for (const sel of selections) {
      const it = itemById.get(sel.patisserieId);
      if (!it) continue;
      const key = it.subcategory || "other";
      if (Number(sel.quantity) > 0) counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [selections, itemById]);

  const subcatWithCounts = useMemo(() => {
    const counts = items.reduce((acc, i) => {
      const key = i?.subcategory || "other";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const known = SUBCAT_ORDER.filter((k) => counts[k]);
    const unknown = Object.keys(counts).filter(
      (k) => !SUBCAT_ORDER.includes(k)
    );
    const order = [...known, ...unknown];
    return order.map((key) => ({
      key,
      label: SUBCATEGORY_META[key]?.label || key,
      count: counts[key],
      hint: SUBCATEGORY_META[key]?.hint || "",
      selected: selectedCountsBySubcat[key] || 0,
    }));
  }, [items, selectedCountsBySubcat]);

  const visibleItems = useMemo(
    () =>
      items.filter((i) =>
        activeSubcat ? i?.subcategory === activeSubcat : true
      ),
    [items, activeSubcat]
  );

  return {
    loading,
    items,
    activeSubcat,
    setActiveSubcat,
    subcatWithCounts,
    visibleItems,
    selections,
    subtotal,
    itemById,
  };
}
